import React, { useMemo, useState } from 'react';

import type {
  IContaDoPlano,
  ICruzamento,
  ICruzamentos,
} from '../../../../../../hooks/useFechamentoContabilMensal';
import { textoDaConta } from '../../regras';
import {
  agruparCruzamentos,
  aparenciaDaSituacao,
  EXPLICACAO_DO_CRUZAMENTO,
  LEITURA_DA_SITUACAO,
  MOTIVO_SEM_DADO,
  num,
} from '../../regrasDoDetalhe';
import { CAIXA, LINK, pilula, SUB, TITULO } from './estilos';

// as seis do layout aparecem sempre; as outras, só quando acontecem
const SEMPRE = ['divergente', 'imaterial', 'sem_dado', 'informativo', 'confere', 'sem_retencao'];
const AS_VEZES = ['nao_comparavel', 'sem_ocorrencia'];

function Comparacao({ item }: { item: ICruzamento }) {
  const ap = aparenciaDaSituacao(item.situacao);
  const maior = Math.max(Math.abs(item.valor_a || 0), Math.abs(item.valor_b || 0), 1);
  const barras = [
    { rotulo: item.rotulo_a, valor: item.valor_a, cor: '#1B88F5' },
    { rotulo: item.rotulo_b, valor: item.valor_b, cor: '#0891B2' },
  ];
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        marginTop: 10,
        paddingTop: 10,
        borderTop: '1px solid #E4E4E7',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 700, minWidth: 0 }}>{item.nome}</span>
        <span style={pilula(ap.suave, ap.cor)}>{ap.rotulo}</span>
      </div>
      {barras.map(b => (
        <div key={b.rotulo} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            title={b.rotulo}
            style={{
              width: '38%',
              fontSize: 11.5,
              color: '#52525B',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {b.rotulo}
          </span>
          <span
            style={{
              flex: 1,
              height: 6,
              borderRadius: 3,
              background: '#EEF2F6',
              overflow: 'hidden',
            }}
          >
            <span
              style={{
                display: 'block',
                height: 6,
                borderRadius: 3,
                width: `${((Math.abs(b.valor || 0) / maior) * 100).toFixed(1)}%`,
                background: b.cor,
              }}
            />
          </span>
          <b style={{ width: 104, textAlign: 'right', fontSize: 12 }}>{num(b.valor)}</b>
        </div>
      ))}
      <div
        style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginTop: 2 }}
      >
        <span style={{ color: '#52525B' }}>Diferença</span>
        <b style={{ color: item.situacao === 'divergente' ? '#B91C1C' : '#0F172A' }}>
          {num(item.diferenca)}
        </b>
      </div>
      {item.observacao && (
        <span style={{ fontSize: 11.5, color: '#52525B' }}>{item.observacao}</span>
      )}
    </div>
  );
}

/** Aba Cruzamentos do detalhe: as comparações da contabilidade com fontes externas. */
export function AbaCruzamentos({
  dados,
  materialidade,
  plano,
}: {
  dados: ICruzamentos;
  materialidade?: number;
  plano?: Record<string, IContaDoPlano>;
}) {
  const [filtro, setFiltro] = useState('');
  const todos = dados.cruzamentos;
  const contar = (s: string) => todos.filter(x => x.situacao === s).length;
  const situacoes = [...SEMPRE, ...AS_VEZES.filter(s => contar(s) > 0)];
  const blocos = useMemo(() => {
    const doFiltro = filtro ? todos.filter(x => x.situacao === filtro) : todos;
    return agruparCruzamentos(doFiltro);
  }, [todos, filtro]);
  const mat = materialidade != null ? num(materialidade) : '—';

  return (
    <>
      <div className="cz3">
        <div style={CAIXA}>
          <div style={TITULO}>Resultado das comparações</div>
          <span style={{ ...SUB, marginBottom: 4 }}>
            Clique numa situação para filtrar os cards.
          </span>
          {situacoes.map(s => {
            const ap = aparenciaDaSituacao(s);
            const qtd = contar(s);
            const ativo = filtro === s;
            return (
              <button
                type="button"
                key={s}
                className="czSit"
                onClick={() => setFiltro(ativo ? '' : s)}
                aria-pressed={ativo}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: 'calc(100% + 12px)',
                  margin: '0 -6px',
                  padding: '4px 6px',
                  border: `1px solid ${ativo ? ap.cor : 'transparent'}`,
                  borderRadius: 8,
                  background: ativo ? ap.suave : 'transparent',
                  cursor: 'pointer',
                  textAlign: 'left',
                  color: '#0F172A',
                }}
              >
                <span style={{ width: 140, flexShrink: 0 }}>
                  <span style={pilula(ap.suave, ap.cor)}>{ap.rotulo}</span>
                </span>
                <span
                  style={{
                    flex: 1,
                    height: 7,
                    borderRadius: 4,
                    background: '#EEF2F6',
                    overflow: 'hidden',
                  }}
                >
                  <span
                    style={{
                      display: 'block',
                      height: 7,
                      borderRadius: 4,
                      width: `${todos.length ? ((qtd / todos.length) * 100).toFixed(1) : 0}%`,
                      background: ap.cor,
                    }}
                  />
                </span>
                <b style={{ width: 24, textAlign: 'right', fontSize: 12.5 }}>{qtd}</b>
              </button>
            );
          })}
          <span style={{ ...SUB, marginTop: 4 }}>
            {todos.length} comparações · materialidade {mat}
          </span>
        </div>
        <div className="czSpan2" style={{ ...CAIXA, gap: 8 }}>
          <div style={TITULO}>O que é um cruzamento</div>
          <span style={SUB}>
            O sistema confronta a contabilidade com fontes externas: notas fiscais, declarações,
            guias pagas, folha e extratos bancários. Diferença acima da materialidade ({mat}) é
            tratada como divergente.
          </span>
          <div className="cz2" style={{ gap: '8px 16px' }}>
            {situacoes.map(s => {
              const ap = aparenciaDaSituacao(s);
              return (
                <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  <span style={pilula(ap.suave, ap.cor)}>{ap.rotulo}</span>
                  <span style={SUB}>{LEITURA_DA_SITUACAO[s]}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {filtro && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            fontSize: 12.5,
            color: '#52525B',
          }}
        >
          <span>
            Mostrando só os cards com{' '}
            <b style={{ color: '#0F172A' }}>{aparenciaDaSituacao(filtro).rotulo}</b>
          </span>
          <button type="button" onClick={() => setFiltro('')} style={{ ...LINK, fontSize: 12.5 }}>
            mostrar todos
          </button>
        </div>
      )}

      <div
        className="czScroll"
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
        role="region"
        aria-label="Cards de cruzamentos"
      >
        {blocos.length === 0 && (
          <p
            style={{
              margin: 0,
              padding: 20,
              textAlign: 'center',
              fontSize: 12.5,
              color: '#52525B',
            }}
          >
            Nenhum cruzamento nesta situação.
          </p>
        )}
        {blocos.map(bloco => (
          <div key={bloco.numero} style={{ ...CAIXA, gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <span
                style={{
                  width: 28,
                  height: 28,
                  flexShrink: 0,
                  borderRadius: 999,
                  background: '#1B6FD0',
                  color: '#FFFFFF',
                  fontSize: 12.5,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {bloco.numero}
              </span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                <div style={TITULO}>{bloco.titulo}</div>
                <span style={SUB}>{EXPLICACAO_DO_CRUZAMENTO[bloco.numero]}</span>
              </div>
            </div>
            <div className="cz2">
              {bloco.grupos.map(grupo => {
                const ap = aparenciaDaSituacao(grupo.pior);
                const titulo = grupo.contas.length
                  ? grupo.contas.map(c => textoDaConta(c, plano)).join(' + ')
                  : grupo.tema;
                return (
                  <div
                    key={grupo.chave}
                    style={{
                      minWidth: 0,
                      padding: '12px 14px',
                      display: 'flex',
                      flexDirection: 'column',
                      background: 'rgba(255, 255, 255, .9)',
                      border: '1px solid #E4E4E7',
                      borderRadius: 12,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: 8,
                      }}
                    >
                      <span style={{ fontSize: 13, fontWeight: 700, minWidth: 0 }}>{titulo}</span>
                      <span style={pilula(ap.suave, ap.cor)}>{ap.rotulo}</span>
                    </div>
                    {grupo.itens.map(item => (
                      <Comparacao
                        key={`${item.nome}|${item.rotulo_a}|${item.rotulo_b}`}
                        item={item}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {dados.cruzamentos_sem_dado.length > 0 && (
          <div style={CAIXA}>
            <div style={TITULO}>Cruzamentos ainda sem dados</div>
            <span style={SUB}>
              Por que cada um não apareceu nesta competência. &quot;Sem ocorrência no mês&quot; não
              é pendência: não havia o que comparar.
            </span>
            <div className="cz2" style={{ columnGap: 24, rowGap: 0 }}>
              {dados.cruzamentos_sem_dado.map(x => {
                const ap = MOTIVO_SEM_DADO[x.situacao] ?? aparenciaDaSituacao('sem_dado');
                const explicacao = EXPLICACAO_DO_CRUZAMENTO[x.numero] ?? '';
                return (
                  <div
                    key={x.numero}
                    style={{
                      padding: '10px 0',
                      borderBottom: '1px solid #E4E4E7',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 4,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: 8,
                      }}
                    >
                      <span style={{ fontSize: 12.5, fontWeight: 700 }}>
                        {x.numero}. {x.titulo}
                      </span>
                      <span style={pilula(ap.suave, ap.cor)}>{ap.rotulo}</span>
                    </div>
                    <span style={{ fontSize: 12 }}>{x.motivo || explicacao}</span>
                    {x.motivo && <span style={SUB}>{explicacao}</span>}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
